import { lazy, Suspense, useContext, useEffect } from 'react';
import { injectContentsquareScript } from '@contentsquare/tag-sdk';
import { useIdleTimer } from 'react-idle-timer';
import { Route, BrowserRouter as Router } from 'react-router-dom';
import { ErrorBoundary, Header, UnauthorizedSplash } from 'buzz_lib_ui';
import { ConfigContext, LoggingContextProvider, UserContextProvider } from 'buzz_lib_contexts';
import { AuthBoundary, AuthBoundaryType, BuzzUserGroup, useAuthContext } from 'buzz_lib_auth';
import Holding from './routes/Holding';
import Home from './routes/Home';
import Loading from './routes/Loading';
import { useHoldingMessage } from './useHoldingMessage';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const refetchSecretsInterval = 1000 * 60 * 2;
const ContentLibrary = /*#__PURE__*/lazy(() => import('./routes/ContentLibrary'));
const Core = /*#__PURE__*/lazy(() => import('./routes/Core'));
const Programming = /*#__PURE__*/lazy(() => import('./routes/Programming'));
const Insights = /*#__PURE__*/lazy(() => import('./routes/Insights'));
const NotFound = /*#__PURE__*/lazy(() => import('./routes/404'));
const DeliveryManager = /*#__PURE__*/lazy(() => import('./routes/DeliveryManager'));
const ImageOrders = /*#__PURE__*/lazy(() => import('./routes/ImageOrders'));
const Licence = /*#__PURE__*/lazy(() => import('./routes/Licence'));
const Tool = /*#__PURE__*/lazy(() => import('./routes/Tool'));
const idleTimeoutMs = 4 * 60 * 60 * 1000; // ms * sec * min * h = 4h

const App = () => {
  const configContext = useContext(ConfigContext);
  const authContext = useAuthContext();
  const secretsQueryResult = configContext === null || configContext === void 0 ? void 0 : configContext.getSecrets(['AppInsightsKey', 'AppVersion', 'HoldingMessage', 'HotjarSiteId']);
  const secrets = secretsQueryResult === null || secretsQueryResult === void 0 ? void 0 : secretsQueryResult.data;
  const {
    getHoldingMessage
  } = useHoldingMessage();
  useIdleTimer({
    crossTab: true,
    timeout: idleTimeoutMs,
    onIdle: () => {
      authContext.logout();
    }
  });
  useEffect(() => {
    if (secrets !== null && secrets !== void 0 && secrets['HotjarSiteId']) {
      injectContentsquareScript({
        siteId: secrets['HotjarSiteId'],
        async: true,
        defer: true
      });
    }
  }, [secrets]);
  useEffect(() => {
    const interval = setInterval(() => {
      getHoldingMessage().then(message => {
        if (message) {
          authContext === null || authContext === void 0 || authContext.logout();
        }
      });
    }, refetchSecretsInterval);
    return () => clearInterval(interval);
  }, []);
  if (secrets !== null && secrets !== void 0 && secrets['HoldingMessage']) {
    return /*#__PURE__*/_jsx(Holding, {
      message: secrets['HoldingMessage']
    });
  }
  const BuzzGroupUnauthorised = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Header, {
      user: authContext.user,
      onLogout: authContext.logout,
      children: "Buzz"
    }), /*#__PURE__*/_jsx(UnauthorizedSplash, {
      onLogout: authContext.logout
    })]
  });
  return /*#__PURE__*/_jsx(AuthBoundary, {
    boundaryType: AuthBoundaryType.Block,
    requiredGroups: [BuzzUserGroup.Buzz],
    unauthorizedComponent: BuzzGroupUnauthorised,
    children: /*#__PURE__*/_jsx(UserContextProvider, {
      user: authContext.user,
      children: /*#__PURE__*/_jsx(Suspense, {
        fallback: /*#__PURE__*/_jsx(Loading, {
          env: configContext === null || configContext === void 0 ? void 0 : configContext.env
        }),
        children: /*#__PURE__*/_jsx(Router, {
          children: /*#__PURE__*/_jsxs(LoggingContextProvider, {
            appinsightsKey: secrets ? secrets['AppInsightsKey'] : '',
            user: authContext.user,
            children: [/*#__PURE__*/_jsx(Route, {
              path: "/",
              exact: true,
              children: /*#__PURE__*/_jsx(Home, {})
            }), /*#__PURE__*/_jsx(Route, {
              path: "/hive",
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                children: /*#__PURE__*/_jsx(ContentLibrary, {})
              })
            }), /*#__PURE__*/_jsx(Route, {
              path: "/core",
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                children: /*#__PURE__*/_jsx(Core, {})
              })
            }), /*#__PURE__*/_jsx(Route, {
              path: "/programming",
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                children: /*#__PURE__*/_jsx(Programming, {})
              })
            }), /*#__PURE__*/_jsx(Route, {
              path: "/insights",
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                children: /*#__PURE__*/_jsx(Insights, {})
              })
            }), /*#__PURE__*/_jsx(Route, {
              path: "/order",
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                children: /*#__PURE__*/_jsx(DeliveryManager, {})
              })
            }), /*#__PURE__*/_jsx(Route, {
              path: "/tool",
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                children: /*#__PURE__*/_jsx(Tool, {})
              })
            }), /*#__PURE__*/_jsx(Route, {
              path: "/image-orders",
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                children: /*#__PURE__*/_jsx(ImageOrders, {})
              })
            }), /*#__PURE__*/_jsx(Route, {
              path: "/licence",
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                children: /*#__PURE__*/_jsx(Licence, {})
              })
            }), /*#__PURE__*/_jsx(Route, {
              component: NotFound
            })]
          })
        })
      })
    })
  });
};
export default App;